/**
 * MSR portal, business-lead review 2026-10-27 (migrations 1150-1152):
 *  - availability = the days a rep CAN work (msr_available_days), one month at a time;
 *  - "Verify a sale" by the member's email (msr_verify_sale);
 *  - typed-in Reinstatements / Upgrades per activity day (msr_manual_sale_counts, temporary until Zuora can verify).
 * Every write goes through an RPC that derives the actor from auth.uid(); the client never sends a user id.
 */
import { supabase } from "./supabase";

/** "MSR_VERIFY_DAY_CLOSED: 2026-10-27 is closed ..." -> "2026-10-27 is closed ..." (the server's sentence). */
export function msrErrorText(
  err: unknown,
  fallback = "Something went wrong. Try again.",
): string {
  const raw =
    (err as { message?: string } | null)?.message ??
    (typeof err === "string" ? err : "");
  if (!raw) {
    return fallback;
  }
  if (/permission denied/i.test(raw)) {
    return "You don't have access to do that.";
  }
  return raw.replace(/^MSR_[A-Z_]+:\s*/, "") || fallback;
}

// ---------------- availability ----------------

export interface AvailablePrompt {
  show: boolean;
  month?: string; // YYYY-MM-01
  prompt_day?: number;
}

export async function getAvailabilityPrompt(): Promise<AvailablePrompt> {
  const { data, error } = await supabase.rpc("msr_availability_prompt");
  if (error) {
    throw error;
  }
  return (data ?? { show: false }) as AvailablePrompt;
}

/** The rep's available days (YYYY-MM-DD) for these positions between from and to, inclusive. */
export async function getMyAvailableDays(
  positionIds: string[],
  from: string,
  to: string,
): Promise<{ sales_position_id: string; day: string }[]> {
  if (!positionIds.length) {
    return [];
  }
  const { data, error } = await supabase
    .from("msr_available_days")
    .select("sales_position_id, day")
    .in("sales_position_id", positionIds)
    .gte("day", from)
    .lte("day", to)
    .order("day")
    .limit(1000);
  if (error) {
    throw error;
  }
  return (data ?? []) as { sales_position_id: string; day: string }[];
}

export async function saveAvailableDays(
  positionId: string,
  month: string,
  days: string[],
): Promise<{ days_in_month: number; past_days_ignored: number }> {
  const { data, error } = await supabase.rpc("msr_set_available_days", {
    p_sales_position_id: positionId,
    p_month: month,
    p_days: days,
  });
  if (error) {
    throw error;
  }
  return data as { days_in_month: number; past_days_ignored: number };
}

export interface TeamAvailabilityRow {
  sales_position_id: string;
  rep_name: string;
  position_title: string | null;
  days: string[];
}

export async function getTeamAvailability(
  month: string,
): Promise<TeamAvailabilityRow[]> {
  const { data, error } = await supabase.rpc("get_msr_team_availability", {
    p_month: month,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []) as TeamAvailabilityRow[];
}

/** positionId -> dates (from `dates`) an MSR has NOT marked available. Non-MSR positions never appear. */
export async function getDaysNotAvailable(
  positionIds: string[],
  dates: string[],
): Promise<Record<string, string[]>> {
  const ids = [...new Set(positionIds.filter(Boolean))].slice(0, 200);
  const ds = [...new Set(dates.filter(Boolean))].sort().slice(0, 62);
  if (!ids.length || !ds.length) {
    return {};
  }
  const { data, error } = await supabase.rpc("msr_days_not_available", {
    p_position_ids: ids,
    p_dates: ds,
  });
  if (error) {
    throw error;
  }
  const out: Record<string, string[]> = {};
  for (const r of (data ?? []) as {
    sales_position_id: string;
    day: string;
  }[]) {
    (out[r.sales_position_id] ??= []).push(r.day);
  }
  for (const k of Object.keys(out)) {
    out[k].sort();
  }
  return out;
}

export async function getMsrPortalSetting(key: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("msr_portal_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data?.value as string | undefined) ?? null;
}

// ---------------- verify a sale ----------------

export type VerifyStatus =
  | "verified"
  | "already_yours"
  | "already_other"
  | "not_verified"
  /** owner 2026-10-30: an active member who joined before the window (or the activity), or has no start date */
  | "not_new_sale"
  | "rate_limited";

export interface VerifyResult {
  status: VerifyStatus;
  message: string;
  attached_to_activity?: boolean;
  service_date?: string | null;
}

export async function verifySale(
  email: string,
  eventId?: string | null,
  serviceDate?: string | null,
): Promise<VerifyResult> {
  const { data, error } = await supabase.rpc("msr_verify_sale", {
    p_email: email,
    p_event_id: eventId ?? null,
    p_service_date: serviceDate ?? null,
  });
  if (error) {
    throw error;
  }
  return data as VerifyResult;
}

// ---------- verify a sale by name / phone / promo code (migrations 1340-1341) ----------
// The server returns at most 10 masked matches (first name, last initial, city/state, member start, masked email),
// each with an opaque single-use token. The rep picks one and confirms it; the client never sends a contact id.

export type SaleSearchKind = "name" | "phone" | "promo";

export interface SaleMatch {
  token: string;
  first_name: string | null;
  last_initial: string | null;
  city: string | null;
  state: string | null;
  member_start: string | null;
  masked_email: string | null;
}

export interface SaleSearchResult {
  status: "matches" | "no_match" | "not_new_sale" | "rate_limited";
  matches: SaleMatch[];
  truncated: boolean;
  limit: number;
  message: string | null;
  service_date?: string | null;
}

export type ConfirmStatus =
  | Exclude<VerifyStatus, "rate_limited">
  | "token_invalid"
  | "token_expired";

export interface ConfirmResult {
  status: ConfirmStatus;
  message: string;
  attached_to_activity?: boolean;
  service_date?: string | null;
}

export async function searchSaleMatches(
  kind: SaleSearchKind,
  query: string,
  eventId?: string | null,
  serviceDate?: string | null,
): Promise<SaleSearchResult> {
  const { data, error } = await supabase.rpc("msr_verify_sale_search", {
    p_kind: kind,
    p_query: query,
    p_event_id: eventId ?? null,
    p_service_date: serviceDate ?? null,
  });
  if (error) {
    throw error;
  }
  const r = (data ?? {}) as Partial<SaleSearchResult>;
  return {
    status: r.status ?? "no_match",
    matches: Array.isArray(r.matches) ? r.matches : [],
    truncated: r.truncated === true,
    limit: r.limit ?? 10,
    message: r.message ?? null,
    service_date: r.service_date ?? null,
  };
}

export async function confirmSaleMatch(token: string): Promise<ConfirmResult> {
  const { data, error } = await supabase.rpc("msr_verify_sale_confirm", {
    p_match_token: token,
  });
  if (error) {
    throw error;
  }
  return data as ConfirmResult;
}

/**
 * The server's name form (_msr_name_norm, owner 2026-10-30): lower case, apostrophes dropped ("O'Neil" = "ONeil"),
 * hyphens / dashes / slashes / underscores = spaces, other punctuation dropped, jr / sr / ii / iii / iv dropped.
 */
export function normalizeName(raw: string): string {
  return ` ${raw
    .toLowerCase()
    .replace(/['’‘`]/g, "")
    .replace(/[-‐‑–—/_]/g, " ")
    .replace(/[^\p{L}\p{N}\s]/gu, "")} `
    .replace(/\s(jr|sr|ii|iii|iv)(?=\s)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Worth sending as-you-type (owner 2026-10-30): no digits / @, and the last word has at least 2 letters. The server
 * returns members only once a FULL last name matches; a partial one returns nothing (and costs no search).
 */
export function nameLooksSearchable(raw: string): boolean {
  if (!raw.trim() || raw.length > 100 || /[0-9@]/.test(raw)) {
    return false;
  }
  const t = normalizeName(raw).split(" ").filter(Boolean);
  return t.length > 0 && t[t.length - 1].length >= 2;
}

/** 10 digits once a leading US 1 and a trailing extension are dropped (the server applies the same rule). */
export function phoneDigits(raw: string): string | null {
  const noExt = raw.replace(/\s*(extension|ext\.?|x|#)\s*\d{1,6}\s*$/i, "");
  const d = noExt.replace(/\D/g, "");
  if (d.length === 10) {
    return d;
  }
  if (d.length === 11 && d.startsWith("1")) {
    return d.slice(1);
  }
  return null;
}

export interface MyVerifiedSale {
  id: string;
  verified_at: string;
  member_email: string | null;
  event_id: string | null;
  event_title: string | null;
  service_date: string | null;
}

export async function getMyVerifiedSales(
  eventId?: string | null,
): Promise<MyVerifiedSale[]> {
  const { data, error } = await supabase.rpc("get_my_verified_sales", {
    p_event_id: eventId ?? null,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []) as MyVerifiedSale[];
}

export interface AdminVerifiedSale {
  id: string;
  verified_at: string;
  rep_name: string | null;
  sales_position_id: string;
  member_name: string | null;
  member_email: string | null;
  /** What the rep typed (immutable). */
  entered_email: string | null;
  membership_start: string;
  event_id: string | null;
  event_title: string | null;
  service_date: string | null;
  removed_at: string | null;
  removed_reason: string | null;
}

export async function getVerifiedSalesAdmin(
  from: string | null,
  to: string | null,
  includeRemoved = false,
): Promise<AdminVerifiedSale[]> {
  const { data, error } = await supabase.rpc("get_msr_verified_sales_admin", {
    p_from: from,
    p_to: to,
    p_include_removed: includeRemoved,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []) as AdminVerifiedSale[];
}

export async function removeVerifiedSale(
  id: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc("msr_remove_verified_sale", {
    p_id: id,
    p_reason: reason,
  });
  if (error) {
    throw error;
  }
}

// ---------------- reinstatements & upgrades (typed in) ----------------

export interface ManualCountRow {
  service_date: string;
  sales_position_id: string;
  rep_name: string | null;
  reinstatements: number;
  upgrades: number;
  is_mine: boolean;
  updated_at: string;
}

export async function getManualCounts(
  eventId: string,
): Promise<ManualCountRow[]> {
  const { data, error } = await supabase.rpc("get_msr_manual_counts", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []) as ManualCountRow[];
}

export async function getActivityDates(eventId: string): Promise<string[]> {
  const { data, error } = await supabase.rpc("get_msr_activity_dates", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []).map((d) =>
    typeof d === "string"
      ? d
      : String((d as Record<string, unknown>)?.get_msr_activity_dates ?? d),
  );
}

export async function setManualCounts(
  eventId: string,
  serviceDate: string,
  reinstatements: number,
  upgrades: number,
): Promise<void> {
  const { error } = await supabase.rpc("msr_set_manual_counts", {
    p_event_id: eventId,
    p_service_date: serviceDate,
    p_reinstatements: reinstatements,
    p_upgrades: upgrades,
  });
  if (error) {
    throw error;
  }
}

export interface AdminManualCountRow {
  event_id: string;
  event_title: string | null;
  service_date: string;
  sales_position_id: string;
  rep_name: string | null;
  reinstatements: number;
  upgrades: number;
  updated_at: string;
}

export async function getManualCountsAdmin(
  from: string | null,
  to: string | null,
): Promise<AdminManualCountRow[]> {
  const { data, error } = await supabase.rpc("get_msr_manual_counts_admin", {
    p_from: from,
    p_to: to,
  });
  if (error) {
    throw error;
  }
  return (Array.isArray(data) ? data : []) as AdminManualCountRow[];
}

export interface MySaleTotals {
  verified_sales: number;
  reinstatements: number;
  upgrades: number;
}

export async function getMySaleTotals(
  from: string | null,
  to: string | null,
): Promise<MySaleTotals> {
  const { data, error } = await supabase.rpc("get_my_msr_sale_totals", {
    p_from: from,
    p_to: to,
  });
  if (error) {
    throw error;
  }
  const row = (Array.isArray(data) ? data : [])[0] ?? {};
  return {
    verified_sales: Number(row.verified_sales ?? 0),
    reinstatements: Number(row.reinstatements ?? 0),
    upgrades: Number(row.upgrades ?? 0),
  };
}

// ---------------- date helpers (calendar dates; no timezone shifts) ----------------

/** Today's date in Central time (the MSR portal's day), YYYY-MM-DD. */
export function centralToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function addMonths(monthIso: string, n: number): string {
  const [y, m] = monthIso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 10);
}

export function monthEnd(monthIso: string): string {
  const next = addMonths(monthIso, 1);
  const d = new Date(`${next}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function monthLabel(monthIso: string): string {
  return new Date(`${monthStart(monthIso)}T12:00:00Z`).toLocaleDateString(
    "en-US",
    {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    },
  );
}

/** Every date from a to b inclusive (calendar dates). */
export function datesBetween(a: string, b: string): string[] {
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  const out: string[] = [];
  const d = new Date(`${lo}T12:00:00Z`);
  for (let i = 0; i < 400; i++) {
    const s = d.toISOString().slice(0, 10);
    if (s > hi) {
      break;
    }
    out.push(s);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** The month the prompt rule targets: next month from the prompt day on, else this month. */
export function availabilityTargetMonth(
  today: string,
  promptDay: number,
): string {
  const day = Number(today.slice(8, 10));
  return day >= promptDay ? addMonths(monthStart(today), 1) : monthStart(today);
}

/** How many months past the current one My Availability can show (msr_set_available_days accepts this month + 12). */
export const AVAILABILITY_MONTHS_AHEAD = 12;

/**
 * The month My Availability opens on (bug 2026-10-30: a rep saw only next month and could not see whether they work
 * today or tomorrow). The `?month=YYYY-MM` link (the availability prompt names next month) wins when it is within
 * this month .. +12; anything else, including no link, opens on THIS month so today is on screen.
 */
export function availabilityPageMonth(
  param: string | null | undefined,
  today: string,
): string {
  const current = monthStart(today);
  if (!param || !/^\d{4}-(0[1-9]|1[0-2])$/.test(param)) {
    return current;
  }
  const asked = `${param}-01`;
  return asked >= current &&
    asked <= addMonths(current, AVAILABILITY_MONTHS_AHEAD)
    ? asked
    : current;
}
